import type { AxiosResponse } from "axios";
import useSWR, { KeyedMutator } from "swr";
import { Analysis } from "@/models/analysis";
import { Voc } from "@/models/Voc";
import {
  Analysis as ApiAnalysis,
  ExecuteAnalysisResponse,
  GetAnalysesResponse,
  GetTotalVocCharCountResponse,
} from "@/services/analysis/analysis.api.types";
import AnalysisEndpoints from "@/services/analysis/analysis.endpoint";
import ApiService, { defaultSWROption } from "@/services/api/api.service";
import { VocSchema } from "@/services/voc/voc.api.types";

export class AnalysisService {
  static getAnalyses(workspaceId: string | null): {
    analyses: Analysis[] | undefined;
    error: unknown;
    isLoading: boolean;
    mutate: KeyedMutator<AxiosResponse<GetAnalysesResponse>>;
  } {
    const fetcher = () =>
      ApiService.fetchData<undefined, GetAnalysesResponse>({
        url: AnalysisEndpoints.GET_ANALYSIS_LIST(workspaceId),
        method: "GET",
      });

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { data, error, isLoading, mutate } = useSWR(
      // 第1引数がnullの場合はAPI呼び出しがスキップされる
      workspaceId ? AnalysisEndpoints.GET_ANALYSIS_LIST(workspaceId) : null,
      fetcher,
      defaultSWROption,
    );

    return {
      analyses: data?.data.analyses.map(buildAnalysis),
      error,
      isLoading,
      mutate,
    };
  }

  static async getTotalVocCharCount(workspaceId: string): Promise<number> {
    const result = await ApiService.fetchData<undefined, GetTotalVocCharCountResponse>({
      url: AnalysisEndpoints.GET_TOTAL_VOC_CHAR_COUNT(workspaceId),
      method: "GET",
    });
    return result.data.totalVocCharCount;
  }

  static async executeAnalysis(
    workspaceId: string,
    title: string,
    startDate: string,
    endDate: string,
    categoryTreeId: string,
    period?: string,
  ): Promise<Analysis> {
    const data = {
      workspaceId,
      title,
      startDate,
      endDate,
      categoryTreeId,
      period,
    };
    const response = await ApiService.fetchData<typeof data, ExecuteAnalysisResponse>({
      url: AnalysisEndpoints.EXECUTE_ANALYSIS,
      data,
      method: "POST",
    });

    return buildAnalysis(response.data);
  }

  static async downloadCsv(
    analysisId: string,
    categoryPath?: string,
  ): Promise<Blob> {
    const response = await ApiService.fetchData<undefined, Blob>({
      url: AnalysisEndpoints.DOWNLOAD_CSV(analysisId, encodeURIComponent(categoryPath ?? "")),
      method: "GET",
      responseType: "blob",
      timeout: 0,
    });

    return response.data;
  }

  static getAnalysisVocs(
    analysisId: string | undefined,
    categoryPath: string | undefined,
  ): {
    vocs: Voc[] | undefined;
    error: unknown;
    isLoading: boolean;
    mutate: KeyedMutator<AxiosResponse<VocSchema[], undefined>>;
  } {
    const fetcher = () =>
      ApiService.fetchData<undefined, VocSchema[]>({
        url: analysisId && categoryPath
          ? AnalysisEndpoints.GET_ANALYSIS_VOCS(analysisId)
          : "",
        method: "GET",
        params: {
          category_path: categoryPath,
        },
      });

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { data, error, isLoading, mutate } = useSWR(
      analysisId && categoryPath
        ? `${
          AnalysisEndpoints.GET_ANALYSIS_VOCS(
            analysisId,
          )
        }?category_path=${categoryPath}`
        : null,
      fetcher,
      defaultSWROption,
    );

    return {
      vocs: data?.data.map(buildVoc),
      error,
      isLoading,
      mutate,
    };
  }
}

const buildAnalysis = (analysis: ApiAnalysis | ExecuteAnalysisResponse) =>
  new Analysis(
    analysis.id,
    analysis.title,
    new Date(analysis.startDate),
    new Date(analysis.endDate),
    analysis.period ?? null,
    analysis.status,
    analysis.categoryTreeId,
    new Date(analysis.createdAt),
    analysis.totalVocCharCount,
  );

const buildVoc = (voc: VocSchema) => new Voc(voc.id, voc.category_path, new Date(voc.recorded_at), voc.voc_text);
